import { useEffect, useState } from 'react';
import { Box, Flex, Stack } from '@chakra-ui/react';

import { Job } from '@/types/Job';
import { getWorkTypeLabel } from '@/types/enums';
import RichEditor from '@/components/forms/RichEditor';

export default function JobSummary({
  job,
  totalCandidates,
  waitingReview,
  PassScreening
}: {
  job: Job;
  totalCandidates: number;
  waitingReview: number;
  PassScreening: number;
}) {
  const [requirements, setRequirements] = useState<string[]>([]);
  const [niceToHaves, setNiceToHaves] = useState<string[]>([]);

  useEffect(() => {
    if (job.requirements) {
      setRequirements(job.requirements.requirements || []);
      setNiceToHaves(job.requirements.nice_to_haves || []);
    }
  }, [job]);

  const formatCurrency = (value: number): string => {
    return value ? `$${value.toLocaleString()}` : '';
  };

  return (
    <Flex direction="column" w="100%" mt="40px">
      <Section title="Metrics">
        <MetricBox title="Total Applicants" value={totalCandidates} />
        <MetricBox title="Waiting Review" value={waitingReview} ml="16px" />
        <MetricBox title="Passed Screening" value={PassScreening} ml="16px" />
      </Section>

      <Section title="Job Details" mt="40px">
        <DetailBox title="Category" value={job.category || '-'} />
        <DetailBox
          title="Salary"
          value={
            job.salary_from && job.salary_to
              ? `${job.currency} ${formatCurrency(job.salary_from)} to ${formatCurrency(
                  job.salary_to
                )}`
              : 'N/A'
          }
        />
        <DetailBox title="Workplace Type" value={getWorkTypeLabel(job.work_type)} />
      </Section>

      <Section title="Job Description" mt="24px">
        <Box p="0">
          <RichEditor
            key="job-description"
            content={job.description}
            type="job-description"
            readOnly
          />
        </Box>
      </Section>

      <Section title="Client Contact" mt="32px">
        <DetailBox title="Name" value={job.contact_name} />
        <DetailBox title="Role" value={job.contact_role} />
      </Section>
      <Section>
        <DetailBox title="Email" value={job.contact_email} />
        <DetailBox title="Phone" value={job.contact_phone} />
      </Section>

      <Section title="AI Analysis" mt="24px" direction="column">
        {['Requirements', 'Nice To Have'].map((section, sectionIndex) => (
          <Box
            key={sectionIndex}
            borderRadius="12px"
            border="1px solid"
            borderColor="neutrals.300"
            width="80%"
            mb="20px"
          >
            <Stack direction="column">
              <Flex
                justifyContent="space-between"
                alignItems="center"
                bg="neutrals.50"
                p="16px 32px"
                borderBottom="1px solid"
                borderColor="neutrals.300"
                borderTopRadius="12px"
              >
                <Box fontWeight="bold" fontSize="18px">
                  {section}
                </Box>
              </Flex>
              {(section === 'Requirements' ? requirements : niceToHaves).map((item, index) => (
                <Flex
                  key={index}
                  justifyContent="space-between"
                  alignItems="center"
                  p="10px 16px 16px 16px"
                  borderBottom={
                    index !==
                    (section === 'Requirements' ? requirements.length : niceToHaves.length) - 1
                      ? '1px solid'
                      : 'none'
                  }
                  borderColor="neutrals.300"
                >
                  <Box ml="16px">{item}</Box>
                </Flex>
              ))}
            </Stack>
          </Box>
        ))}
      </Section>
    </Flex>
  );
}

function Section({
  title = null,
  children,
  mt = '0',
  direction = 'row'
}: {
  title?: string | null;
  children: React.ReactNode;
  mt?: string;
  direction?: 'column' | 'row';
}) {
  return (
    <Box mt={mt}>
      {title && (
        <Box color="neutrals.900" fontSize="24px" fontWeight="semibold">
          {title}
        </Box>
      )}
      <Box h="24px" />
      <Flex direction={direction}>{children}</Flex>
    </Box>
  );
}

function MetricBox({ title, value, ml = '0' }: { title: string; value: number; ml?: string }) {
  return (
    <Box w="33.33%" bg="neutrals.50" borderRadius="12px" p="16px" ml={ml}>
      <Box color="neutrals.700" fontSize="18px" fontWeight={500}>
        {title}
      </Box>
      <Box h="14px" />
      <Box color="neutrals.900" fontSize="28px" fontWeight="bold">
        {value}
      </Box>
    </Box>
  );
}

function DetailBox({ title, value }: { title: string; value: string }) {
  return (
    <Box w="50%">
      <Box color="neutrals.900" fontSize="16px" fontWeight="semibold">
        {title}
      </Box>
      <Box h="12px" />
      <Box color="neutrals.700" fontSize="16px" fontWeight="regular">
        {value || 'N/A'}
      </Box>
    </Box>
  );
}
